import axios from 'axios';
import router from '@/router';
import store from '@/store'; // 确保导入 store

// 设置基础URL
const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5050/api';

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response && error.response.status === 401) {
      // 清除本地存储的认证信息
      store.dispatch('logout'); // 使用 store action 来处理登出逻辑
      // 跳转到登录页面，确保当前不是登录页，避免无限循环
      if (router.currentRoute.value.name !== 'LoginRegister') {
         router.push('/login');
      }
    }
    return Promise.reject(error);
  }
);

// --- AuthService ---
export const authService = {
  login: async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        // 可以在这里直接commit到store或在调用处处理
      }
      return response.data;
    } catch (error) {
      console.error("Login failed:", error.response?.data || error.message);
      throw error;
    }
  },
  register: async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
       console.error("Register failed:", error.response?.data || error.message);
       throw error;
    }
  },
  // logout 在 store action 中处理，这里不需要
};

// --- UserService ---
export const userService = {
  getUserInfo: async () => {
    try {
      const response = await apiClient.get('/users/me');
      return response.data;
    } catch (error) {
      console.error("Get user info failed:", error.response?.data || error.message);
      throw error;
    }
  },
  updateUserInfo: async (userData) => {
    try {
      const response = await apiClient.put('/users/me', userData);
      // 更新本地存储的用户信息
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { ...user, ...response.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      store.commit('updateUser', updatedUser); // 更新Vuex状态
      return response.data;
    } catch (error) {
      console.error("Update user info failed:", error.response?.data || error.message);
      throw error;
    }
  },
  changePassword: async (passwordData) => {
    try {
      const response = await apiClient.put('/users/me/password', passwordData);
      return response.data;
    } catch (error) {
      console.error("Change password failed:", error.response?.data || error.message);
      throw error;
    }
  },
  getFavorites: async () => {
    try {
      const response = await apiClient.get('/users/me/favorites');
      return response.data.favorites; // 后端返回的是 {'favorites': [...]}
    } catch (error) {
      console.error("Get favorites failed:", error.response?.data || error.message);
      throw error;
    }
  },
  deleteUser: async () => {
     try {
      const response = await apiClient.delete('/users/me');
      store.dispatch('logout'); // 删除用户后需要登出
      return response.data;
    } catch (error) {
      console.error("Delete user failed:", error.response?.data || error.message);
      throw error;
    }
  }
};

// --- WordService ---
export const wordService = {
  getWords: async (page = 1, perPage = 20, query = null, level = null) => {
    try {
      const params = { page, per_page: perPage };
      if (query) params.query = query;
      if (level) params.level = level;
      const response = await apiClient.get('/words', { params });
      return response.data; // 后端返回结构 { words: [], total: n, pages: n, current_page: n }
    } catch (error) {
      console.error("Get words failed:", error.response?.data || error.message);
      throw error;
    }
  },
  getWord: async (wordId) => {
    try {
      const response = await apiClient.get(`/words/${wordId}`);
      return response.data; // 后端直接返回单词对象 (包含 meanings)
    } catch (error) {
      console.error(`Get word ${wordId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  getWordLists: async (page = 1, perPage = 20) => {
    try {
      const response = await apiClient.get('/words/lists', { params: { page, per_page: perPage } });
      return response.data; // 后端返回结构 { word_lists: [], total: n, pages: n, current_page: n }
    } catch (error) {
      console.error("Get word lists failed:", error.response?.data || error.message);
      throw error;
    }
  },
  getWordList: async (listId, withWords = false) => {
    try {
      const response = await apiClient.get(`/words/lists/${listId}`, { params: { with_words: withWords } });
      return response.data; // 返回单词本对象，可能包含 words 列表
    } catch (error) {
      console.error(`Get word list ${listId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  toggleFavorite: async (wordId, isFavorite) => {
    try {
      const response = await apiClient.post(`/words/favorite/${wordId}`, { is_favorite: isFavorite });
      return response.data; // { message: '...', is_favorite: true/false }
    } catch (error) {
      console.error(`Toggle favorite ${wordId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  checkFavorite: async (wordId) => {
    try {
      const response = await apiClient.get(`/words/favorite/${wordId}`);
      return response.data.is_favorite; // 后端返回 { is_favorite: true/false }
    } catch (error) {
      console.error(`Check favorite ${wordId} failed:`, error.response?.data || error.message);
      // 如果请求失败，可以默认返回未收藏状态
      return false;
      // 或者再次抛出错误让调用者处理
      // throw error;
    }
  },
  // 其他 Word/Meaning/WordList 的 CRUD 操作按需添加
  // createWord, updateWord, deleteWord, addMeaning, etc.
};

// --- LearningService ---
export const learningService = {
  getLearningQueue: async (selectedWordListId, practiceMode = null, limit = null) => {
    try {
      const params = { selected_word_list_id: selectedWordListId };
      if (practiceMode) params.practice_mode = practiceMode;
      if (limit) params.limit = limit;
      const response = await apiClient.get('/learning/queue', { params });
      return response.data; // { queue: [], total_count: n }
    } catch (error) {
      console.error("Get learning queue failed:", error.response?.data || error.message);
      throw error;
    }
  },
  getUserProgress: async (options = {}) => {
    try {
      const { selectedWordListId, practiceMode, wordId, status, page = 1, perPage = 20 } = options;
      const params = { page, per_page: perPage };
      if (selectedWordListId) params.selected_word_list_id = selectedWordListId;
      if (practiceMode) params.practice_mode = practiceMode;
      if (wordId) params.word_id = wordId;
      if (status) params.status = status;
      const response = await apiClient.get('/learning/progress', { params });
      return response.data; // { progress: [], total: n, pages: n, current_page: n }
    } catch (error) {
      console.error("Get user progress failed:", error.response?.data || error.message);
      throw error;
    }
  },
  recordWordProgress: async (wordId, data) => {
     // data 应包含: selected_word_list_id, practice_mode, is_correct, [response_quality], [is_marked_difficult]
    try {
      const response = await apiClient.post(`/learning/progress/${wordId}`, data);
      return response.data; // 返回更新后的进度对象
    } catch (error) {
      console.error(`Record progress ${wordId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  markDifficult: async (wordId, selectedWordListId, practiceMode, isDifficult = true) => {
    try {
      const response = await apiClient.put(`/learning/difficult/${wordId}`, {
        selected_word_list_id: selectedWordListId,
        practice_mode: practiceMode,
        is_difficult: isDifficult
      });
      return response.data; // 返回更新后的进度对象
    } catch (error) {
      console.error(`Mark difficult ${wordId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  getLearningStatistics: async (selectedWordListId = null, practiceMode = null) => {
    try {
      const params = {};
      if (selectedWordListId) params.selected_word_list_id = selectedWordListId;
      if (practiceMode) params.practice_mode = practiceMode;
      const response = await apiClient.get('/learning/statistics', { params });
      return response.data; // 返回统计数据对象
    } catch (error) {
      console.error("Get statistics failed:", error.response?.data || error.message);
      throw error;
    }
  },
  generateQuestion: async (wordId, selectedWordListId, practiceMode) => {
    try {
      const params = { word_id: wordId, selected_word_list_id: selectedWordListId, practice_mode: practiceMode };
      const response = await apiClient.get('/learning/generate_question', { params });
      return response.data; // 返回题目数据对象
    } catch (error) {
      console.error("Generate question failed:", error.response?.data || error.message);
      throw error;
    }
  },
  skipWord: async (wordId, selectedWordListId, practiceMode, delayDays = 1) => {
    try {
      const response = await apiClient.post(`/learning/skip/${wordId}`, {
        selected_word_list_id: selectedWordListId,
        practice_mode: practiceMode,
        delay_days: delayDays
      });
      return response.data; // { message: '...', next_review_at: '...', delay_days: n }
    } catch (error) {
      console.error(`Skip word ${wordId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  getPracticeModes: async () => {
    try {
      const response = await apiClient.get('/learning/practice-modes');
      return response.data.practice_modes; // { practice_modes: [{ value: '...', name: '...' }] }
    } catch (error) {
      console.error("Get practice modes failed:", error.response?.data || error.message);
      throw error;
    }
  },
  getDailyGoalStatus: async (selectionId) => {
     // 注意：后端路由是 /learning/user-learning/... 但逻辑上属于learning service
    try {
      const response = await apiClient.get(`/learning/user-learning/selections/${selectionId}/daily-status`);
      return response.data; // 返回每日目标状态对象
    } catch (error) {
      console.error(`Get daily goal status ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  }
};

// --- UserLearningService ---
export const userLearningService = {
  getLearningLists: async () => { // 获取用户选择的单词本列表
    try {
      const response = await apiClient.get('/user-learning/lists');
      return response.data; // { word_lists: [], total: n }
    } catch (error) {
      console.error("Get user learning lists failed:", error.response?.data || error.message);
      throw error;
    }
  },
  selectWordList: async (wordListId, data) => { // 选择一个单词本开始学习（创建 UserSelectedWordList）
     // data: { name?, daily_new_word_goal?, daily_review_goal?, practice_modes? }
    try {
      const response = await apiClient.post(`/user-learning/wordlists/${wordListId}/select`, data);
      return response.data; // 返回创建的 selection 对象
    } catch (error) {
      console.error(`Select word list ${wordListId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  updateSelectionSettings: async (selectionId, data) => { // 更新某个选择的设置
    // data: { name?, daily_new_word_goal?, daily_review_goal?, is_active? }
    try {
      const response = await apiClient.put(`/user-learning/selections/${selectionId}`, data);
      return response.data; // 返回更新后的 selection 对象
    } catch (error) {
      console.error(`Update selection ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  removeSelection: async (selectionId) => { // 停止学习某个选择（删除 UserSelectedWordList）
    try {
      const response = await apiClient.delete(`/user-learning/selections/${selectionId}`);
      return response.data; // { message: '...' }
    } catch (error) {
      console.error(`Remove selection ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  getSelectionPracticeModes: async (selectionId) => { // 获取某个选择的练习模式配置
    try {
      const response = await apiClient.get(`/user-learning/selections/${selectionId}/modes`);
      return response.data; // { practice_modes: [], selection_id: n, ... }
    } catch (error) {
      console.error(`Get modes for selection ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  addPracticeMode: async (selectionId, practiceMode) => { // 为某个选择添加练习模式
    try {
      const response = await apiClient.post(`/user-learning/selections/${selectionId}/modes`, { practice_mode: practiceMode });
      return response.data; // 返回创建的 practice mode 对象
    } catch (error) {
      console.error(`Add mode ${practiceMode} to selection ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  updatePracticeMode: async (selectionId, practiceMode, isActive) => { // 更新某个选择的练习模式状态
    try {
      const response = await apiClient.put(`/user-learning/selections/${selectionId}/modes/${practiceMode}`, { is_active: isActive });
      return response.data; // 返回更新后的 practice mode 对象
    } catch (error) {
      console.error(`Update mode ${practiceMode} for selection ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  },
  removePracticeMode: async (selectionId, practiceMode) => { // 移除某个选择的练习模式
    try {
      const response = await apiClient.delete(`/user-learning/selections/${selectionId}/modes/${practiceMode}`);
      return response.data; // { message: '...' }
    } catch (error) {
      console.error(`Remove mode ${practiceMode} from selection ${selectionId} failed:`, error.response?.data || error.message);
      throw error;
    }
  }
};

// 可以根据需要导出所有服务或单个服务
// export default { authService, userService, ... };